import { useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.web";
import Turnstile, { type TurnstileHandle } from "../lib/Turnstile";
import { authCodeFor, authMessageFor, type AuthErrorCode } from "../lib/errorCodes";
import BrandHeader from "../lib/BrandHeader";

export default function LoginPage() {
  const nav = useNavigate();
  const location = useLocation() as unknown as { state?: { from?: string } };

  const returnTo = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    const v = sp.get("returnTo");
    return v && v.startsWith("/") ? v : null;
  }, []);

  const force = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("force") === "1";
  }, []);

  // Deliberately does nothing but keep the flag readable.
  //
  // This used to call supabase.auth.signOut(), which defaults to scope
  // "global" and revokes every refresh token the user has, on every device -
  // including the extension's. The popup links here as "Connect / Login", so
  // the button a user presses when they look signed out was the thing that
  // genuinely signed them out everywhere.
  //
  // Nothing needs signing out. This page always renders the form - it never
  // redirects an already-signed-in user - and signing in replaces whatever
  // session the page holds. There is nothing for "force" to force.
  void force;

  const from = useMemo(
    () => returnTo ?? location.state?.from ?? "/connect",
    [location.state, returnTo]
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AuthErrorCode | null>(null);

  // Captcha in GoTrue is global, not per-route: turning it on for signup turns
  // it on for `/token?grant_type=password` too, which is this form. Measured on
  // the running service - without a token this page gets
  // `400 captcha_failed` and no login is possible at all.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstile = useRef<TurnstileHandle | null>(null);

  const verifyLink = useMemo(() => {
    const e = email.trim();
    return `/auth/verify?email=${encodeURIComponent(e)}&returnTo=${encodeURIComponent(from)}`;
  }, [email, from]);

  const canSubmit = !!captchaToken && !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!canSubmit) return;
    setBusy(true);

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
      options: { captchaToken: captchaToken ?? undefined },
    });

    setBusy(false);

    if (error) {
      // The token is spent whether the login succeeded or not, so a second
      // attempt needs a fresh challenge. Without this reset the retry fails
      // with `timeout-or-duplicate` and looks like a wrong password.
      turnstile.current?.reset();
      setCaptchaToken(null);

      // By code, in this app's words (Phase 12; errorCodes.ts). An unknown
      // address and a wrong password are one GoTrue code, invalid_credentials,
      // and "not confirmed" comes only after the RIGHT password - both measured
      // - so nothing shown here says whether an address is registered.
      setError(authCodeFor(error));
      return;
    }

    nav(from, { replace: true });
  }

  // Decided by the code. It used to search GoTrue's English for "not confirmed".
  const showVerifyHint = error === "EMAIL_NOT_CONFIRMED";

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface shadow p-6">
        <BrandHeader />
        <h1 className="text-xl font-semibold">Log in</h1>

        <form className="mt-6 space-y-3" onSubmit={onSubmit}>
          <div>
            <label className="text-sm font-medium" htmlFor="email">Email</label>
            <input
              id="email"
              className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="password">Password</label>
            <input
              id="password"
              className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="pt-1">
            <Turnstile ref={turnstile} onToken={setCaptchaToken} />
          </div>

          {error && (
            <div className="rounded-xl border border-danger-line bg-danger-bg p-3 text-sm text-danger space-y-2">
              <div>{authMessageFor(error)}</div>
              {showVerifyHint && (
                <div className="text-sm">
                  <Link className="text-link-strong hover:underline" to={verifyLink}>
                    Verify your email / resend verification
                  </Link>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="mt-2 w-full rounded-xl bg-primary text-on-primary py-2 disabled:opacity-60"
          >
            {busy ? "Logging in…" : "Log in"}
          </button>

          {/* No standing hint under the button (11.3). The widget above says
              what it wants, and the one case a hint really covered - a check
              that never loaded, leaving the button dead with no explanation -
              is now said by the widget itself, only when it is true. See
              TURNSTILE_FAILED in Turnstile.tsx. The submit rule is untouched:
              no token, no login. */}

          <div className="flex items-center justify-between text-sm">
            <span className="text-fg-soft">
              Don&rsquo;t have an account?{" "}
              <Link className="text-link hover:underline" to="/auth/signup">
                Sign up
              </Link>
            </span>
            {/* /auth/forgot, not /auth/reset. This pointed at the LANDING page
                for four phases, which can do nothing without a recovery session
                - so the one link a locked-out user presses led to a form that
                could never submit (14.2, Phase 10.5). */}
            <Link className="text-link hover:underline" to="/auth/forgot">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
